/* Answers — the verdict queue.
   No mockup exists for this screen; the visual reference is `docs/design/visits.html` (the same
   archetype) and the normative rules are `docs/design/spec/`.

   Shape (spec/03 archetype A): page head → filter card → status segment + derived chips → table →
   sticky bulk bar. Filters apply as they change and everything narrows on the SERVER — the page
   only ever holds one page of rows, so it must never filter or sort them itself.

   Read this before changing the query. The screen this replaced (`/task-review`) ran on
   `answers/photos_on_review/`, which hardcodes `is_supervised=None, is_active=True` and drops
   every answer without a photo — so the list could only ever show the pending, photographed ones:
   no way to see what you had already decided, no counts, no sorting. And its Supervisor control
   sent `visit__point__responsible_supervisor`, which `AnswerFilter` never declared, so
   django-filter dropped it silently and the filter narrowed nothing.

   This runs on plain `GET answers/` with `AnswerWebFilter` (picsell-api
   `tasks/api/web/v1/filters.py`): `status` over the `annotate_answer_approval` subquery, a
   supervisor that works, multi-value points/networks/merchandisers/templates, `has_photo` and
   `negative` for the chips, plus `status_counts` on the paginated response for the segment. */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { Button, Card, Col, Empty, Flex, Form, Image, Input, Row, Segmented, Space, Table, Tag, Typography } from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ClusterOutlined,
  CompassOutlined,
  ExportOutlined,
  IdcardOutlined,
  OrderedListOutlined,
  PushpinOutlined,
  SearchOutlined,
  TeamOutlined,
} from "@ant-design/icons";

import Alert from "/src/util/alert";

import EntitySelect from "../../component/EntitySelect";
import FilterChips, { useLabelCache } from "../../component/FilterChips";
import PeriodPicker, { clampRange, defaultRange } from "../../component/PeriodPicker";
import RejectTaskModal from "../../component/RejectTaskModal";
import { PersonCell, RowActions, StatusIcon, TableFoot, TitleCell, actionsColumn, statusColumn, useTableScrollY } from "../../component/table-cells";
import { ANSWER_LIST, ENTITY_DROPDOWN_SETTINGS, PHOTO_REPORT, SIDE_NAV, VISIT_PAGE } from "../../config/lang";
import { getAbortable } from "../../util/action";
import dayjs from "../../util/dayjs";
import { fill, regionId, translate } from "../../util/util";
import { localeFor } from "../merchandiser/route/dateUtils";
import { postApproval, statusOf } from "../visit/parts";

import "./answers.scss";

const PAGE_SIZES = [10, 20, 50, 100];

/** Widest period the list will accept, in calendar months — the cap `/photo-review` and `/task`
    both enforce, and the default window is the shared `defaultRange()` so the pages cannot drift.
    Held in three places: the calendar greys out days beyond it, `searchToState` clamps a pasted
    URL, and `buildQuery` clamps whatever is in state at request time — the last is what holds. */
const MAX_RANGE_MONTHS = 3;

/* The working-set split. Values are the server's own `status` keys. */
const STATUS_SEGMENTS = [
  ["all", "seg_all"],
  ["pending", "seg_pending"],
  ["approved", "seg_approved"],
  ["rejected", "seg_rejected"],
];

const STATUS_ICON = {
  pending: { Icon: ClockCircleOutlined, tone: "wait", key: "on_verification_single_visit_name" },
  approved: { Icon: CheckCircleOutlined, tone: "ok", key: "approved_single_visit_name" },
  rejected: { Icon: CloseCircleOutlined, tone: "no", key: "no_approved_single_visit_name" },
};

/* The derived, counted first-class filters (spec/03: "a filter without a count makes the user
   click to discover emptiness"). `param` is what the server calls it. */
const CHIPS = [
  { key: "has_photo", param: "has_photo", count: "has_photo", label: "chip_photo", hint: "chip_photo_hint" },
  { key: "negative", param: "negative", count: "negative", label: "chip_negative", hint: "chip_negative_hint" },
];

/* Column key → the field the API orders on. A key missing here is NOT sortable. */
const ORDERING = {
  status: "approval_is_approved",
  point: "visit__point__name",
  by: "visit__user__first_name",
  when: "created",
};

/* Six tracks with the period spanning two — eight units, and 24 does not divide by eight into the
   same track width, so the tracks are percentages. `0 0` rather than `1 1`: a short last row
   leaves empty tracks instead of stretching the fields it does have. */
const FIELD_COL = { xs: { flex: "0 0 100%" }, sm: { flex: "0 0 50%" }, md: { flex: "0 0 33.3333%" }, xl: { flex: "0 0 12.5%" } };
const PERIOD_COL = { xs: { flex: "0 0 100%" }, sm: { flex: "0 0 100%" }, md: { flex: "0 0 66.6667%" }, xl: { flex: "0 0 25%" } };

const blankFilter = () => ({ county: "", network: "", template: "", merchandiser: "", supervisor: [], points: [], search: "" });

const SINGLE_KEYS = ["county", "network", "template", "merchandiser"];
const ARRAY_KEYS = ["supervisor", "points"];

/** Which ENTITY_DROPDOWN_SETTINGS key names each filter's chip. */
const CHIP_LABEL = {
  county: "region",
  network: "network",
  template: "template",
  merchandiser: "merchandisers",
  supervisor: "supervisor",
  points: "points",
};

/* One request per record — there is no bulk approval endpoint — in batches, so a 100-row selection
   does not open 100 sockets at once. */
const BULK_CHUNK = 6;

const idOf = v => (v && typeof v === "object" ? v.id ?? v.value : v);
const joinIds = list =>
  (list || [])
    .map(idOf)
    .filter(v => v !== undefined && v !== null && v !== "")
    .join(",");

const isoDay = d => dayjs(d).format("YYYY-MM-DD");

/**
 * The URL of one photo, whatever shape it arrives in.
 *
 * ⚠ Three shapes are real, and assuming one is why the thumbnails rendered broken:
 * - `AnswerListSerializer` flattens each photo to a plain URL **string**;
 * - `AnswerSerializer` (the detail, and the list until picsell-api ships) nests
 *   `AnswerPhotoSerializer` — an **object**;
 * - and an `AnswerPhoto` itself holds a photo in **either** `photo_url` (hosted elsewhere, what
 *   the mobile app usually writes) or `photo` (an uploaded file). Reading only `photo_url` shows
 *   nothing for every file-backed photo.
 */
export const photoUrl = photo => {
  if (!photo) return null;
  if (typeof photo === "string") return photo;
  return photo.photo_url || photo.photo || null;
};

/** Every photo on an answer: the `AnswerPhoto` rows, plus the legacy single `Answer.photo`. */
export const photosOf = row => [...(row?.photos || []).map(photoUrl), ...(row?.photo ? [photoUrl(row.photo)] : [])].filter(Boolean);

/* The row's point and merchandiser. The list serializer lifts both to the top level; the full
   `AnswerSerializer` (which is what this endpoint returns until picsell-api ships) carries them
   under `visit`, and an answer with no visit falls back to the task's point. */
export const pointOf = row => row?.point || row?.visit?.point || row?.task?.point || null;
export const merchandiserOf = row => row?.merchandiser || row?.visit?.user || null;
/** `locality_name` on the list serializer, a nested `locality` on the full one. */
export const localityOf = point => point?.locality_name || point?.locality?.name || null;

/**
 * The request the table runs on.
 *
 * Built here rather than through `filterUrl()` so the per-field shape the endpoint expects is
 * visible in one place — and because `filterUrl` interpolates its search term unencoded. Exported
 * because `/answers/:id` re-runs it with `page_size=1` to step through this same queue.
 *
 * The period matches on `created`: an answer's only date is when it arrived.
 */
export function buildQuery({ filter, range, status, chips, page, size, sort }) {
  const p = new URLSearchParams();
  const period = clampRange(range, MAX_RANGE_MONTHS);
  p.set("page", String(page));
  p.set("page_size", String(size));
  /* A deactivated answer is not part of any working set here, and there is no segment for one —
     the legacy endpoint filtered it out server-side and nothing asked for them. */
  p.set("is_active", "true");

  if (filter.county) p.set("county", String(idOf(filter.county)));
  if (filter.network) p.set("networks", String(idOf(filter.network)));
  if (filter.template) p.set("templates", String(idOf(filter.template)));
  if (filter.merchandiser) p.set("merchandisers", String(idOf(filter.merchandiser)));
  if (filter.supervisor?.length) p.set("supervisor", joinIds(filter.supervisor));
  if (filter.points?.length) p.set("points", joinIds(filter.points));
  if (filter.search?.trim()) p.set("search", filter.search.trim());
  if (status !== "all") p.set("status", status);

  /* Unchecked sends NOTHING. `false` means "no constraint" on the server, but an absent param is
     unambiguous either way — and `has_photo=false` is a real filter ("answers with nothing to
     look at"), so sending it by accident would be a silent narrowing. */
  CHIPS.forEach(chip => chips.includes(chip.key) && p.set(chip.param, "true"));

  if (period?.[0]) p.set("created__gte", isoDay(period[0]));
  /* Inclusive end: `created__lte` against a bare date is midnight, so the picked day itself would
     drop every answer that did not arrive at exactly 00:00. */
  if (period?.[1]) p.set("created__lte", isoDay(dayjs(period[1]).add(1, "day")));
  if (sort) p.set("ordering", `${sort.dir === "descend" ? "-" : ""}${ORDERING[sort.key]}`);

  return p.toString();
}

/** URL ↔ state, so a filtered view is shareable and survives Back from an answer's page. */
export function stateToSearch({ filter, range, status, chips, page, size, sort }) {
  const p = new URLSearchParams();
  if (range?.[0]) p.set("from", isoDay(range[0]));
  if (range?.[1]) p.set("to", isoDay(range[1]));
  SINGLE_KEYS.forEach(key => {
    if (filter[key]) p.set(key, String(idOf(filter[key])));
  });
  ARRAY_KEYS.forEach(key => (filter[key] || []).forEach(v => p.append(key, String(idOf(v)))));
  if (filter.search?.trim()) p.set("q", filter.search.trim());
  if (status !== "all") p.set("status", status);
  chips.forEach(key => p.append("chip", key));
  if (page > 1) p.set("page", String(page));
  if (size !== PAGE_SIZES[0]) p.set("size", String(size));
  if (sort) p.set("sort", `${sort.dir === "descend" ? "-" : ""}${sort.key}`);
  return p.toString();
}

export function searchToState(search, me) {
  const p = new URLSearchParams(search);
  const filter = blankFilter();
  SINGLE_KEYS.forEach(key => {
    if (p.get(key)) filter[key] = p.get(key);
  });
  /* Ids only — `EntitySelect` names them once it has fetched the option they belong to. */
  ARRAY_KEYS.forEach(key => {
    filter[key] = p.getAll(key).map(v => ({ id: Number(v) }));
  });
  filter.search = p.get("q") || "";

  const hasFilters = Boolean(search && search.replace(/^\?/, ""));
  /* Without a URL to restore, the supervisor's own region is the scope they are responsible for —
     the same default `/visit` and `/task` seed. */
  if (!hasFilters) filter.county = regionId(me) || "";

  const from = p.get("from");
  const to = p.get("to");
  const sort = p.get("sort");
  return {
    filter,
    range: from && to ? clampRange([dayjs(from), dayjs(to)], MAX_RANGE_MONTHS) : defaultRange(),
    status: STATUS_SEGMENTS.some(([value]) => value === p.get("status")) ? p.get("status") : "pending",
    chips: p.getAll("chip").filter(key => CHIPS.some(chip => chip.key === key)),
    page: Math.max(1, Number(p.get("page")) || 1),
    size: PAGE_SIZES.includes(Number(p.get("size"))) ? Number(p.get("size")) : PAGE_SIZES[0],
    sort: sort && ORDERING[sort.replace(/^-/, "")] ? { key: sort.replace(/^-/, ""), dir: sort[0] === "-" ? "descend" : "ascend" } : null,
  };
}

const List = () => {
  const history = useHistory();
  const location = useLocation();
  const me = useSelector(state => state.me);
  const lang = me?.language || "en";

  const t = key => translate(ANSWER_LIST, key, lang);
  const td = key => translate(ENTITY_DROPDOWN_SETTINGS, key, lang);
  const tp = key => translate(PHOTO_REPORT, key, lang);
  const tv = key => translate(VISIT_PAGE, key, lang);

  /* Read once. After mount this component owns the state and writes the URL, not the reverse —
     reading it back on every change would fight the user's own typing. */
  const initial = useRef(searchToState(location.search, me)).current;

  const [filter, setFilter] = useState(initial.filter);
  const [range, setRangeState] = useState(initial.range);
  /* A cleared period returns to the default window instead of "all time": the cap exists so this
     list never asks for a year of answers, and clearing to nothing walks straight past it. */
  const setRange = next => setRangeState(clampRange(next, MAX_RANGE_MONTHS) || defaultRange());
  const [status, setStatus] = useState(initial.status);
  const [chips, setChips] = useState(initial.chips);
  const [page, setPage] = useState(initial.page);
  const [size, setSize] = useState(initial.size);
  const [sort, setSort] = useState(initial.sort);

  /* What the user is typing, kept apart from `filter.search` so the request is debounced without
     the input lagging a keystroke behind the caret. */
  const [term, setTerm] = useState(initial.filter.search);

  /* The option objects the selects handed back, so a chosen value labels itself. */
  const [chosen, setChosen] = useState({ county: null, network: null, template: null, merchandiser: null });

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  /* The row a rejection is being written for, or null. */
  const [rejecting, setRejecting] = useState(null);

  /* --------------------------------------------------------------- fetch */

  const query = useMemo(() => buildQuery({ filter, range, status, chips, page, size, sort }), [filter, range, status, chips, page, size, sort]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    getAbortable(`answers/?${query}`, controller.signal)
      .then(response => {
        if (!response) return;
        setRows(response.results || []);
        setTotal(response.count || 0);
        if (response.status_counts) setCounts(response.status_counts);
        setLoading(false);
      })
      .catch(error => {
        if (error.name === "AbortError") return;
        setRows([]);
        setTotal(0);
        setLoading(false);
      });
    return () => controller.abort();
  }, [query]);

  /* The URL trails the state; `replace` so filtering does not fill the history stack and Back
     still leaves the page rather than undoing one filter at a time. */
  useEffect(() => {
    const search = stateToSearch({ filter, range, status, chips, page, size, sort });
    if (search !== location.search.replace(/^\?/, "")) history.replace({ search });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, range, status, chips, page, size, sort]);

  /* Only the free-text field is debounced; every other control applies on change. */
  useEffect(() => {
    if (term === filter.search) return;
    const timer = setTimeout(() => {
      setPage(1);
      setSelected([]);
      setFilter(prev => ({ ...prev, search: term }));
    }, 250);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term]);

  /* Any narrowing invalidates the page number: page 7 of the old result set is usually past the
     end of the new one, which would render an empty table over a non-empty result. */
  const narrow = fn => (...args) => {
    setPage(1);
    setSelected([]);
    fn(...args);
  };

  const setSingle = narrow((key, option) => {
    setChosen(prev => ({ ...prev, [key]: option }));
    setFilter(prev => ({ ...prev, [key]: option ? idOf(option) : "" }));
  });
  const setArray = narrow((key, list) => setFilter(prev => ({ ...prev, [key]: list })));
  const toggleChip = narrow(key => setChips(prev => (prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key])));

  const resetFilters = narrow(() => {
    setFilter(blankFilter());
    setChosen({ county: null, network: null, template: null, merchandiser: null });
    setRange(defaultRange());
    setStatus("pending");
    setChips([]);
    setTerm("");
    setSort(null);
  });

  /* ---------------------------------------------------------------- chips */

  const { labels, remember, nameFor } = useLabelCache();

  const clearChip = narrow((key, id) => {
    if (key === "period") {
      setRange(defaultRange());
      return;
    }
    if (key === "search") {
      setTerm("");
      setFilter(prev => ({ ...prev, search: "" }));
      return;
    }
    if (SINGLE_KEYS.includes(key)) {
      setChosen(prev => ({ ...prev, [key]: null }));
      setFilter(prev => ({ ...prev, [key]: "" }));
      return;
    }
    setFilter(prev => ({ ...prev, [key]: (prev[key] || []).filter(v => String(idOf(v)) !== String(id)) }));
  });

  const activeChips = useMemo(() => {
    const out = [];
    if (range?.[0] && range?.[1]) {
      /* Short form, per spec/04 — `28 Jul → 11 Aug`. The year shows only when the range leaves the
         current one, which is also what keeps the chip on one line on a phone. */
      const thisYear = new Date().getFullYear();
      const sameYear = range[0].year() === thisYear && range[1].year() === thisYear;
      const fmt = new Intl.DateTimeFormat(localeFor(lang), { day: "numeric", month: "short", ...(sameYear ? {} : { year: "numeric" }) });
      out.push({ key: "period", label: tp("period"), value: `${fmt.format(range[0].toDate())} → ${fmt.format(range[1].toDate())}` });
    }
    if (filter.search) out.push({ key: "search", label: tp("search"), value: filter.search });
    SINGLE_KEYS.forEach(key => {
      if (!filter[key]) return;
      /* `chosen[key]` is the option object the select handed back, so it names itself; falling back
         to the bare id sends `nameFor` to the resolution cache. */
      out.push({ key, label: td(CHIP_LABEL[key]), value: nameFor(key, chosen[key] ?? filter[key]) });
    });
    ARRAY_KEYS.forEach(key => {
      (filter[key] || []).forEach(v => out.push({ key, id: idOf(v), label: td(CHIP_LABEL[key]), value: nameFor(key, v) }));
    });
    return out;
    /* `labels` is a dependency: a chip that arrived as an id has to re-render once its name lands. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, chosen, labels, range, lang]);

  /* ------------------------------------------------------- approve / reject */

  const approve = row =>
    new Promise(resolve => {
      postApproval({ objectId: row.id, objectType: "answer", isApproved: true, comment: "" }, ok => resolve(ok));
    });

  /* Rows are patched in place rather than refetched, so the table does not jump under the cursor,
     and the counts are moved by hand for the same reason. A row that no longer matches the active
     segment stays until the next fetch: its glyph has already changed, so it is showing the result
     of the action rather than a stale state. */
  const applyLocally = (ids, isApproved) => {
    const affected = rows.filter(row => ids.includes(row.id));
    setRows(prev => prev.map(row => (ids.includes(row.id) ? { ...row, approval: { is_approved: isApproved, comment: row.approval?.comment || "" } } : row)));
    setCounts(prev => {
      const next = { ...prev };
      const to = isApproved ? "approved" : "rejected";
      affected.forEach(row => {
        const from = statusOf(row.approval);
        if (from === to) return;
        next[from] = Math.max(0, (next[from] || 0) - 1);
        next[to] = (next[to] || 0) + 1;
      });
      return next;
    });
  };

  const approveOne = async row => {
    setBusy(true);
    const ok = await approve(row);
    setBusy(false);
    if (!ok) return;
    applyLocally([row.id], true);
    Alert.success(tv("record_approved"), { position: "top-right" });
  };

  const approveSelected = async () => {
    const targets = rows.filter(row => selected.includes(row.id) && statusOf(row.approval) !== "approved");
    if (!targets.length) {
      setSelected([]);
      return;
    }
    setBusy(true);
    const done = [];
    for (let i = 0; i < targets.length; i += BULK_CHUNK) {
      const batch = targets.slice(i, i + BULK_CHUNK);
      const results = await Promise.all(batch.map(approve));
      batch.forEach((row, index) => results[index] && done.push(row.id));
    }
    setBusy(false);
    if (done.length) {
      applyLocally(done, true);
      Alert.success(fill(t("approved_msg"), { n: done.length }), { position: "top-right" });
    }
    if (done.length < targets.length) {
      Alert.warning(fill(t("failed_msg"), { n: targets.length - done.length }), { position: "top-right" });
    }
    setSelected([]);
  };

  /* Walk into the record carrying this row's position in the FILTERED list and the filters
     themselves, so the workspace can step through the same queue and come back to this view. */
  const openAnswer = (row, indexOnPage) => {
    const search = new URLSearchParams(stateToSearch({ filter, range, status, chips, page, size, sort }));
    if (indexOnPage != null) {
      search.set("i", String((page - 1) * size + indexOnPage + 1));
      search.set("n", String(total));
    }
    history.push(`/answers/${row.id}?${search.toString()}`);
  };

  /* ------------------------------------------------------------- columns */

  const dateFmt = useMemo(() => new Intl.DateTimeFormat(localeFor(lang), { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }), [lang]);

  const columns = useMemo(
    () => [
      statusColumn((value, row) => {
        const { Icon, tone, key } = STATUS_ICON[statusOf(row.approval)];
        return <StatusIcon Icon={Icon} tone={tone} label={tv(key)} />;
      }),
      {
        key: "point",
        title: t("col_point"),
        width: 300,
        fixed: "left",
        ellipsis: true,
        sorter: true,
        render: (value, row) => {
          const point = pointOf(row);
          return <TitleCell title={[localityOf(point), point?.name].filter(Boolean).join(" · ")} sub={row.task?.title} />;
        },
      },
      {
        key: "answer",
        title: t("col_answer"),
        width: 320,
        render: (value, row) => {
          const photos = photosOf(row);
          const cover = photos[0];
          /* Boolean first: a "no" is the operational signal on this row, and it reads as a word
             plus a tone rather than as a bare true/false. */
          const text = row.answer || (row.boolean_answer === null ? null : t(row.boolean_answer ? "ans_yes" : "ans_no"));
          return (
            <Flex align="center" gap={8} className="an-ans">
              {/* No `preview`: the row's own click opens the record, and a lightbox opening
                  underneath a navigation is two things happening at once. */}
              {cover ? <Image src={cover} preview={false} width={36} height={36} rootClassName="an-thumb" alt="" /> : null}
              <Flex vertical className="lp-main">
                <Typography.Text ellipsis={{ tooltip: text }} className={row.boolean_answer === false ? "an-ans__no" : ""}>
                  {text || <span className="lp-dash">{t("ans_photo_only")}</span>}
                </Typography.Text>
                {photos.length > 1 ? (
                  <Typography.Text type="secondary" className="lp-main__sub">
                    {fill(t("photos_count"), { n: photos.length })}
                  </Typography.Text>
                ) : null}
              </Flex>
            </Flex>
          );
        },
      },
      {
        key: "by",
        title: t("col_by"),
        width: 200,
        ellipsis: true,
        sorter: true,
        render: (value, row) => <PersonCell user={merchandiserOf(row)} strong={false} />,
      },
      {
        key: "when",
        title: t("col_when"),
        width: 170,
        sorter: true,
        render: (value, row) => <span className="lp-num">{row.created ? dateFmt.format(new Date(row.created)) : "—"}</span>,
      },
      actionsColumn((value, row, index) => {
        /* The verdict items appear only while the answer is still pending — the legacy menu
           offered Approve and Reject on every row regardless of what had already been decided. */
        const pending = statusOf(row.approval) === "pending";
        return (
          <RowActions
            label={t("row_actions")}
            items={[
              { key: "open", icon: <ExportOutlined />, label: t("open") },
              ...(pending
                ? [
                    { key: "approve", icon: <CheckCircleOutlined />, label: tv("approv_btn") },
                    { key: "reject", icon: <CloseCircleOutlined />, label: tv("reject_btn"), danger: true },
                  ]
                : []),
            ]}
            onClick={key => {
              if (key === "open") return openAnswer(row, index);
              if (key === "approve") return approveOne(row);
              setRejecting(row);
            }}
          />
        );
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lang, rows, dateFmt]
  );

  const scrollY = useTableScrollY();
  const selectable = rows.filter(row => selected.includes(row.id) && statusOf(row.approval) === "pending");

  return (
    <Flex vertical gap="middle" className="shell-content lp an">
      <Flex className="lp-head" justify="space-between" align="flex-end" gap="middle" wrap>
        <Flex vertical>
          <Typography.Title level={1} className="lp-title">
            {translate(SIDE_NAV, "answers", lang)}
          </Typography.Title>
          <Typography.Paragraph type="secondary" className="lp-sub">
            {t("page_sub")}
          </Typography.Paragraph>
        </Flex>
      </Flex>

      <Card className="lp-filters" variant="outlined">
        {/* `component={false}`: there is nothing to submit — filters apply as they change, and a
            real <form> would turn Enter in the search box into a page reload. */}
        <Form layout="vertical" component={false} colon={false}>
          <Row gutter={[12, 12]}>
            <Col {...PERIOD_COL}>
              <Form.Item label={tp("period")}>
                {/* No `allowFuture`: an answer's date is when it arrived, and nothing arrives
                    tomorrow. */}
                <PeriodPicker value={range} onChange={narrow(setRange)} maxMonths={MAX_RANGE_MONTHS} clearTo={defaultRange} />
              </Form.Item>
            </Col>
            <Col {...FIELD_COL}>
              <Form.Item label={td("region")}>
                <EntitySelect
                  entity="county"
                  value={filter.county}
                  seed={chosen.county || (me?.county && typeof me.county === "object" ? me.county : null)}
                  prefix={<CompassOutlined />}
                  onChange={option => setSingle("county", option)}
                  onResolve={remember("county")}
                />
              </Form.Item>
            </Col>
            <Col {...FIELD_COL}>
              <Form.Item label={td("network")}>
                <EntitySelect
                  entity="network"
                  value={filter.network}
                  seed={chosen.network}
                  prefix={<ClusterOutlined />}
                  onChange={option => setSingle("network", option)}
                  onResolve={remember("network")}
                />
              </Form.Item>
            </Col>
            <Col {...FIELD_COL}>
              {/* The template is what the merchandiser was asked for, so it is what makes one
                  answer comparable with another. */}
              <Form.Item label={td("template")}>
                <EntitySelect
                  entity="template"
                  value={filter.template}
                  seed={chosen.template}
                  prefix={<OrderedListOutlined />}
                  onChange={option => setSingle("template", option)}
                  onResolve={remember("template")}
                />
              </Form.Item>
            </Col>
            <Col {...FIELD_COL}>
              <Form.Item label={td("merchandisers")}>
                <EntitySelect
                  entity="merchandisers"
                  value={filter.merchandiser}
                  seed={chosen.merchandiser}
                  prefix={<IdcardOutlined />}
                  onChange={option => setSingle("merchandiser", option)}
                  onResolve={remember("merchandiser")}
                />
              </Form.Item>
            </Col>
            {/* Only a manager sees every supervisor's answers, so only a manager can filter by one —
                the same gate `/visit` and `/task` apply. */}
            {me?.role === "supervisor_manager" ? (
              <Col {...FIELD_COL}>
                <Form.Item label={td("supervisor")}>
                  <EntitySelect
                    entity="supervisor"
                    isMulti
                    value={filter.supervisor}
                    prefix={<TeamOutlined />}
                    onChange={list => setArray("supervisor", list)}
                    onResolve={remember("supervisor")}
                  />
                </Form.Item>
              </Col>
            ) : null}
            <Col {...FIELD_COL}>
              {/* Scoped by the two filters beside it — `EntitySelect` sends `locality__county` and
                  `network`, so the list offers the points the rest of the row implies. */}
              <Form.Item label={td("points")}>
                <EntitySelect
                  entity="points"
                  isMulti
                  value={filter.points}
                  county={filter.county}
                  moduleFilter={filter.network}
                  prefix={<PushpinOutlined />}
                  onChange={list => setArray("points", list)}
                  onResolve={remember("points")}
                />
              </Form.Item>
            </Col>
            <Col {...FIELD_COL}>
              <Form.Item label={tp("search")}>
                <Input allowClear prefix={<SearchOutlined />} placeholder={t("search_ph")} value={term} onChange={event => setTerm(event.target.value)} />
              </Form.Item>
            </Col>
          </Row>

          {/* What is actually applied, in words — the selects collapse to `+N` once a filter holds
              more than one value, so without this row the only way to read the current query is to
              open each dropdown. */}
          <FilterChips className="lp-chips" items={activeChips} closeLabel={tp("a_remove_filter")} onClose={clearChip} />

          {/* The derived filters, each with its count. `Tag.CheckableTag` rather than a second
              Segmented because they combine: with a photo AND negative is a real question. */}
          <Flex className="lp-quality" align="center" gap="small" wrap>
            {CHIPS.map(chip => (
              <Tag.CheckableTag key={chip.key} checked={chips.includes(chip.key)} onChange={() => toggleChip(chip.key)} title={t(chip.hint)}>
                <Space size={6}>
                  {t(chip.label)}
                  <span className="lp-num">{counts[chip.count] ?? 0}</span>
                </Space>
              </Tag.CheckableTag>
            ))}
          </Flex>

          <Flex className="lp-filters__foot" align="center" gap="small" wrap>
            <Segmented
              value={status}
              onChange={narrow(setStatus)}
              options={STATUS_SEGMENTS.map(([value, key]) => ({
                value,
                label: (
                  <Space size={6}>
                    {t(key)}
                    <Typography.Text type="secondary" className="lp-seg__n">
                      {counts[value] ?? 0}
                    </Typography.Text>
                  </Space>
                ),
              }))}
            />
            <span className="lp-spacer" />
            <Button size="small" onClick={resetFilters}>
              {td("filter_reset")}
            </Button>
          </Flex>
        </Form>
      </Card>

      <Card className="lp-table" variant="outlined">
        <Flex className="lp-table__bar" align="center" gap="small" wrap>
          <Typography.Text strong className="lp-table__title">
            {translate(SIDE_NAV, "answers", lang)}
          </Typography.Text>
          <Typography.Text type="secondary" aria-live="polite">
            {tp("records_count")}: <Typography.Text strong>{total}</Typography.Text>
          </Typography.Text>
        </Flex>

        <Table
          className="lp-table__grid"
          rowKey="id"
          size="middle"
          columns={columns}
          dataSource={rows}
          loading={loading}
          scroll={{ x: 1140, y: scrollY }}
          rowClassName={row => (selected.includes(row.id) ? "lp-row lp-row--on" : "lp-row")}
          rowSelection={{
            fixed: true,
            selectedRowKeys: selected,
            onChange: keys => setSelected(keys),
            /* A checkbox here means exactly one thing: this answer still needs a verdict. */
            getCheckboxProps: row => ({ disabled: statusOf(row.approval) !== "pending" }),
          }}
          onRow={(row, index) => ({
            onClick: event => {
              /* The selection cell and the row menu own their clicks; everything else walks into
                 the record. */
              if (event.target.closest(".ant-table-selection-column, .ant-dropdown, .ant-btn")) return;
              openAnswer(row, index);
            },
          })}
          /* Paging is the footer below, so the only thing left for the table's own onChange is the
             sorter — with `pagination={false}` its first argument carries nothing. */
          onChange={(pagination, filters, sorter) => {
            const next = sorter?.order && ORDERING[sorter.columnKey] ? { key: sorter.columnKey, dir: sorter.order } : null;
            if (JSON.stringify(next) === JSON.stringify(sort)) return;
            setSort(next);
            setPage(1);
          }}
          pagination={false}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <Flex vertical gap={8} align="center">
                    <Typography.Text strong>{t("empty_title")}</Typography.Text>
                    <Typography.Text type="secondary">{t("empty_desc")}</Typography.Text>
                    <Button size="small" onClick={resetFilters}>
                      {td("filter_reset")}
                    </Button>
                  </Flex>
                }
              />
            ),
          }}
        />

        <TableFoot
          id="an"
          page={page}
          size={size}
          total={total}
          sizes={PAGE_SIZES}
          labels={{ perPage: t("rows_per_page"), range: t("range_of") }}
          onPage={next => {
            setPage(next);
            setSelected([]);
          }}
          onSize={next => {
            setSize(next);
            setPage(1);
            setSelected([]);
          }}
        />
      </Card>

      {selected.length > 0 && (
        <Flex className="lp-bulkbar" align="center" gap="middle" wrap>
          <Typography.Text className="lp-bulkbar__n">{fill(tp("w_selected"), { n: selected.length })}</Typography.Text>
          {/* "Select all" is the LOADED page, so the bar says so rather than implying the filter. */}
          <Typography.Text className="lp-bulkbar__hint">{t("select_page_hint")}</Typography.Text>
          <Space className="lp-bulkbar__acts" size={8}>
            <Button size="small" onClick={() => setSelected([])}>
              {tp("clear_selection")}
            </Button>
            {/* Approve only. A rejection needs a reason and a corrective task — one photo at a
                time — so there is nothing sensible to apply to a whole selection at once. */}
            <Button size="small" className="an-btn--ok" icon={<CheckCircleOutlined />} loading={busy} disabled={!selectable.length} onClick={approveSelected}>
              {tv("approv_btn")}
            </Button>
          </Space>
        </Flex>
      )}

      {rejecting && (
        <RejectTaskModal
          target={{ objectId: rejecting.id, objectType: "answer" }}
          photo={photosOf(rejecting)[0] || null}
          point={pointOf(rejecting) ? { ...pointOf(rejecting), locality: { name: localityOf(pointOf(rejecting)) } } : null}
          visitId={rejecting.visit_id}
          lang={lang}
          onCancel={() => setRejecting(null)}
          onDone={() => {
            applyLocally([rejecting.id], false);
            setRejecting(null);
          }}
        />
      )}
    </Flex>
  );
};

export default List;
